import React, { useState, useRef, useEffect } from 'react';
import { Send, ArrowLeft, Leaf, Sparkles, Crown, Lock, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API } from '../contexts/AuthContext';

const suggestions = [
  'What helps with sleep?',
  'A safe oil for a toddler’s cough',
  'How do I make an elderberry syrup?',
  'Something calming for anxiety'
];

function getSessionId() {
  let id = localStorage.getItem('botanica_sage_session');
  if (!id) {
    id = 'sage_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('botanica_sage_session', id);
  }
  return id;
}

function formatReset(seconds) {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 1) return `${h}h ${m}m`;
  if (m >= 1) return `${m}m`;
  return 'less than a minute';
}

function formatCountdown(seconds) {
  if (!seconds || seconds <= 0) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export default function AskSage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [status, setStatus] = useState(null); // {unlimited, can_ask, reset_seconds, is_premium}
  const [remaining, setRemaining] = useState(0);
  const sessionId = useRef(getSessionId());
  const endRef = useRef(null);

  useEffect(() => { if (messages.length) endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages, typing]);

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API}/sage/status?session_id=${encodeURIComponent(sessionId.current)}`, {
        credentials: 'include'
      });
      if (res.ok) setStatus(await res.json());
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadStatus(); }, []);

  // sync countdown whenever status changes
  useEffect(() => {
    if (status && !status.unlimited && !status.can_ask) {
      setRemaining(status.reset_seconds || 0);
    } else {
      setRemaining(0);
    }
  }, [status]);

  // live tick every second while locked
  useEffect(() => {
    if (remaining <= 0) return;
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          loadStatus(); // unlock refresh
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [remaining > 0]);

  const unlimited = status?.unlimited;
  const blocked = status ? !status.unlimited && !status.can_ask && remaining > 0 : false;

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || typing || blocked) return;
    setMessages((m) => [...m, { role: 'user', content }]);
    setInput('');
    setTyping(true);

    try {
      const res = await fetch(`${API}/sage/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId.current, message: content })
      });

      if (res.status === 429) {
        setTyping(false);
        let reset = 0;
        try {
          const data = await res.json();
          reset = data?.detail?.reset_seconds || 0;
        } catch (e) { /* ignore */ }
        setStatus({ unlimited: false, can_ask: false, reset_seconds: reset, is_premium: false });
        setMessages((m) => [...m, {
          role: 'sage',
          content: "You've used your free Sage question for today. Upgrade to Premium for unlimited conversations."
        }]);
        return;
      }

      if (!res.ok || !res.body) throw new Error('bad response');

      // add empty sage bubble, then stream tokens into it
      setMessages((m) => [...m, { role: 'sage', content: '' }]);
      setTyping(false);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'sage', content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
      // refresh limit status after a successful message
      loadStatus();
    } catch (e) {
      setTyping(false);
      setMessages((m) => [...m, { role: 'sage', content: 'Sorry, Sage is resting right now. Please try again in a moment.' }]);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 py-6 flex flex-col min-h-[calc(100vh-4rem)]" data-testid="ask-sage-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-botanica-muted hover:text-botanica-ink transition-colors" data-testid="sage-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="w-10 h-10 rounded-full bg-botanica-greenLight flex items-center justify-center">
            <Leaf className="w-5 h-5 text-botanica-green" />
          </span>
          <div>
            <h1 className="font-serif text-xl text-botanica-ink leading-none">Sage</h1>
            <p className="text-xs text-botanica-muted mt-0.5">Your AI herbal companion</p>
          </div>
        </div>
        {unlimited ? (
          <span className="flex items-center gap-1.5 text-xs text-botanica-green bg-botanica-greenLight px-3 py-1.5 rounded-full font-medium" data-testid="sage-limit-badge">
            <Crown className="w-3.5 h-3.5" /> Unlimited
          </span>
        ) : blocked ? (
          <button
            onClick={() => navigate('/membership')}
            className="flex items-center gap-1.5 text-xs text-botanica-greenDark bg-botanica-card px-3 py-1.5 rounded-full font-medium hover:bg-botanica-panel transition-colors tabular-nums"
            data-testid="sage-limit-badge"
            title="Time until your next free question"
          >
            <Clock className="w-3.5 h-3.5" /> {formatCountdown(remaining)}
          </button>
        ) : (
          <span className="text-xs text-botanica-muted bg-botanica-card/70 px-3 py-1.5 rounded-full" data-testid="sage-limit-badge">1 free today · Upgrade</span>
        )}
      </div>

      <div className="flex-1 space-y-4 overflow-y-auto pb-4">
        <div className="bg-botanica-greenLight rounded-2xl px-5 py-4">
          <p className="font-medium text-botanica-ink mb-1">Welcome, friend.</p>
          <p className="text-sm text-botanica-greenDark leading-relaxed">
            Ask me about herbs, oils, recipes, or remedies. I'll point you to the plants and suggest safe approaches.
          </p>
          <p className="text-sm text-botanica-greenDark/80 mt-3">I cannot diagnose or replace medical care.</p>
        </div>

        {messages.length === 0 && !blocked && (
          <div className="flex flex-wrap gap-2 pt-1">
            {suggestions.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-botanica-card/70 text-botanica-ink hover:bg-botanica-card transition-colors"
              >
                <Sparkles className="w-3 h-3 text-botanica-green" /> {s}
              </button>
            ))}
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-line leading-relaxed ${
                m.role === 'user' ? 'bg-botanica-green text-white' : 'bg-botanica-card text-botanica-ink'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}

        {typing && (
          <div className="flex justify-start">
            <div className="bg-botanica-card rounded-2xl px-4 py-3 flex gap-1">
              {[0, 1, 2].map((d) => (
                <span key={d} className="w-2 h-2 rounded-full bg-botanica-muted animate-bounce" style={{ animationDelay: `${d * 150}ms` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={endRef} />

        {blocked && (
          <div className="bg-botanica-card rounded-2xl px-5 py-6 text-center" data-testid="sage-limit-cta">
            <span className="w-12 h-12 rounded-full bg-botanica-greenLight flex items-center justify-center mx-auto mb-3">
              <Crown className="w-6 h-6 text-botanica-green" />
            </span>
            <p className="font-serif text-lg text-botanica-ink mb-1">You've used your free question for today</p>
            <p className="text-sm text-botanica-muted mb-1">
              Your next free question unlocks in
            </p>
            <p className="text-2xl font-serif text-botanica-greenDark tabular-nums mb-4" data-testid="sage-countdown">{formatCountdown(remaining)}</p>
            <p className="text-sm text-botanica-muted mb-4">Or upgrade to Premium for unlimited conversations with Sage.</p>
            <button
              onClick={() => navigate('/membership')}
              className="inline-flex items-center gap-2 bg-botanica-green text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-botanica-greenDark transition-colors"
              data-testid="sage-upgrade-btn"
            >
              <Crown className="w-4 h-4" /> Upgrade to Premium
            </button>
          </div>
        )}
      </div>

      <div className="sticky bottom-0 pt-3 bg-botanica-bg">
        <div className={`flex items-center gap-2 rounded-full pl-5 pr-2 py-2 border ${blocked ? 'bg-botanica-card/40 border-botanica-card/60' : 'bg-botanica-card/70 border-botanica-card'}`}>
          {blocked && <Lock className="w-4 h-4 text-botanica-muted" />}
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder={blocked ? 'Upgrade to Premium to keep chatting…' : 'Ask Sage anything...'}
            disabled={blocked}
            className="flex-1 bg-transparent text-sm text-botanica-ink placeholder:text-botanica-muted focus:outline-none disabled:cursor-not-allowed"
            data-testid="sage-input"
          />
          <button
            onClick={() => (blocked ? navigate('/membership') : send())}
            className="w-10 h-10 rounded-full bg-botanica-green text-white flex items-center justify-center hover:bg-botanica-greenDark transition-colors"
            data-testid="sage-send-btn"
          >
            {blocked ? <Crown className="w-4 h-4" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
        <p className="text-[11px] text-botanica-muted text-center mt-2">Educational only. Not medical advice.</p>
      </div>
    </div>
  );
}
